import { useState, useEffect } from "react";
import { getCurrentUser, changePassword, type AuthUser } from "../../api/auth";
import { validatePassword } from "../../utils/passwordValidation";

type ProfilePageProps = {
  userName?: string;
};

export function ProfilePage({ userName = "Utilisateur" }: ProfilePageProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    getCurrentUser()
      .then((userData) => {
        setUser(userData);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    const validation = validatePassword(value);
    setValidationErrors(validation.errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (newPassword !== confirmPassword) {
      setErrorMessage("Les mots de passe ne correspondent pas.");
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.valid) {
      setErrorMessage(validation.errors.join(" "));
      return;
    }

    setIsSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccessMessage("Mot de passe modifié avec succès.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowChangePassword(false);
    } catch (error) {
      setErrorMessage((error as Error).message || "Échec du changement de mot de passe");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted">Chargement...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-text">Profil</h1>
        <p className="mt-1 text-sm text-muted">Gérez vos informations personnelles et votre sécurité</p>
      </div>

      <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-text mb-4">Informations personnelles</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-text-muted mb-1">Nom</label>
            <p className="text-sm text-text">{user?.display_name || userName || "-"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-muted mb-1">Email</label>
            <p className="text-sm text-text">{user?.email || "-"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-muted mb-1">Statut</label>
            <p className="text-sm text-text">{user?.status || "-"}</p>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text">Sécurité</h2>
          {!showChangePassword && (
            <button
              type="button"
              onClick={() => setShowChangePassword(true)}
              className="px-4 py-2 text-sm font-medium rounded-md bg-accent text-text-inverted hover:opacity-90"
            >
              Changer mon mot de passe
            </button>
          )}
        </div>

        {showChangePassword && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Mot de passe actuel
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full h-10 px-3 rounded-md border border-border bg-surface text-text"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => handleNewPasswordChange(e.target.value)}
                required
                autoComplete="new-password"
                minLength={12}
                className="w-full h-10 px-3 rounded-md border border-border bg-surface text-text"
                disabled={isSubmitting}
              />
              {validationErrors.length > 0 && (
                <ul className="mt-1 text-xs text-danger list-disc list-inside">
                  {validationErrors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Confirmer le nouveau mot de passe
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={12}
                className="w-full h-10 px-3 rounded-md border border-border bg-surface text-text"
                disabled={isSubmitting}
              />
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="mt-1 text-xs text-danger">Les mots de passe ne correspondent pas.</p>
              )}
            </div>

            {errorMessage && (
              <div className="rounded-md border border-danger bg-danger/10 p-3">
                <div className="text-sm text-danger">{errorMessage}</div>
              </div>
            )}

            {successMessage && (
              <div className="rounded-md border border-green-500 bg-green-500/10 p-3">
                <div className="text-sm text-green-600 dark:text-green-400">{successMessage}</div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                  setErrorMessage("");
                  setSuccessMessage("");
                }}
                className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text"
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium rounded-md bg-accent text-text-inverted hover:opacity-90 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Modification..." : "Modifier le mot de passe"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
